import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { chartColor, DONUT_COLOR_COUNT, donutColor, donutOtherColor } from '../../shared/components/charts/chart-palette';
import { ChartPointModel }                                            from '../../shared/components/charts/typings/chart-point';
import { ChartSeriesModel }                                           from '../../shared/components/charts/typings/chart-series';
import { DonutSegmentModel }                                          from '../../shared/components/charts/typings/donut-segment';
import { DropdownOptionModel }                                        from '../../shared/components/dropdown/typings/dropdown-option';
import { IntelligenceCustomerFiltersModel }                           from '../typings/intelligence.customer-filters';
import { IntelligenceCustomersModel }                                 from '../typings/intelligence.customers';
import { IntelligenceFormatService }                                  from '../intelligence.format.service';
import { IntelligenceService }                                        from '../intelligence.service';
import { IntelligenceTopCustomerModel }                               from '../typings/intelligence.top-customer';
import { TableSortModel }                                             from '../../shared/components/charts/typings/table-sort';

@Component({
  selector   : 'wor-intelligence-customers-tab',
  template: require('./intelligence.customers-tab.component.html')
})
export class CustomersTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public filterOptions         : { customers : Array<string>; sources : Array<string>; suburbs : Array<string> } = null;
  public filters               : IntelligenceCustomerFiltersModel = { customer: '', customer_type: '', job_type: '', source: '', suburb: '' };
  public invoiceSourceSegments : Array<DonutSegmentModel> = [];
  public jobMixSegments        : Array<DonutSegmentModel> = [];
  public newVsReturningPoints  : Array<ChartPointModel> = [];
  public retentionValue        : string = '';
  public sort                  : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  public sourceSegments        : Array<DonutSegmentModel> = [];
  public topCustomers          : Array<IntelligenceTopCustomerModel> = [];
  public topCustomerTotals = { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 };
  public visitsValue           : string = '';

  public customerTypeOptions : Array<DropdownOptionModel> = [
    { label: this.translateService.instant('INTELLIGENCE_SPACE.FILTERS.CUSTOMER_TYPE_ALL'), value: '' },
    { label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.NEW_CUSTOMERS'), value: 'new' },
    { label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.RETURNING'), value: 'returning' }
  ];

  public jobTypeOptions : Array<DropdownOptionModel> = [
    { label: this.translateService.instant('INTELLIGENCE_SPACE.FILTERS.JOB_TYPE_ALL'), value: '' },
    { label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.SERVICES'), value: 'services' },
    { label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.REPAIRS'), value: 'repairs' },
    { label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'), value: 'other' }
  ];

  public newVsReturningSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'new_customers', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.NEW_CUSTOMERS') },
    { color: chartColor(2), key: 'returning_customers_invoiced', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.RETURNING') }
  ];

  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  averageInvoice ( customer : IntelligenceTopCustomerModel ) : number {
    return customer.invoice_count
      ? customer.revenue_ex_tax / customer.invoice_count
      : 0;
  }

  clearFilters () : void {
    this.filters = { customer: '', customer_type: '', job_type: '', source: '', suburb: '' };

    this.load();
  }

  hasActiveFilters () : boolean {
    return Object.keys(this.filters).some(key => !!this.filters[ key ]);
  }

  formatMoney ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  isInvoiceSourcesEmpty () : boolean {
    return !this.invoiceSourceSegments.some(segment => segment.value > 0);
  }

  isJobMixEmpty () : boolean {
    return !this.jobMixSegments.some(segment => segment.value > 0);
  }

  isNewVsReturningEmpty () : boolean {
    return !this.newVsReturningPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isSourcesEmpty () : boolean {
    return !this.sourceSegments.some(segment => segment.value > 0);
  }

  isTopCustomersEmpty () : boolean {
    return !this.topCustomers.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getCustomers(this.filters)
    .then(( data : IntelligenceCustomersModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      // a response without filter options keeps the last lists instead of blanking the dropdowns
      if ( data.filter_options ) {
        this.filterOptions = {
          customers: data.filter_options.customers || [],
          sources  : data.filter_options.sources || [],
          suburbs  : data.filter_options.suburbs || []
        };

        if ( this.dropFiltersMissingFromOptions() ) {
          this.load();

          return;
        }
      }

      this.newVsReturningPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          new_customers               : day.new_customers,
          returning_customers_invoiced: day.returning_customers_invoiced
        }
      }));

      this.retentionValue = this.intelligenceFormatService.percent(data.retention_rate_pct);
      this.visitsValue = ( data.avg_visits_per_year || 0 ).toFixed(1);

      this.sourceSegments = this.buildSourceSegments(data.sources || {}, true, this.filterOptions.sources);
      this.invoiceSourceSegments = this.buildSourceSegments(data.invoice_sources || {}, true, this.filterOptions.sources);

      const mix = data.job_mix || { other: 0, repairs: 0, services: 0 };

      this.jobMixSegments = [
        { color: chartColor(1), isDrillable: true, key: 'services', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.SERVICES'), value: mix.services },
        { color: chartColor(2), isDrillable: true, key: 'repairs', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.REPAIRS'), value: mix.repairs },
        { color: chartColor(0), isDrillable: true, key: 'other', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'), value: mix.other }
      ];

      this.topCustomers = this.intelligenceFormatService.sortRows(data.top_customers || [], this.sort.field, this.sort.direction);
      this.topCustomerTotals = this.topCustomers.reduce(( totals, customer ) => ({
        gross_profit_ex_tax: totals.gross_profit_ex_tax + customer.gross_profit_ex_tax,
        invoice_count      : totals.invoice_count + customer.invoice_count,
        revenue_ex_tax     : totals.revenue_ex_tax + customer.revenue_ex_tax
      }), { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 });
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  jobTypeLabel () : string {
    const match = this.jobTypeOptions.find(option => option.value === this.filters.job_type);

    return match && match.value
      ? match.label
      : '';
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setFilter ( field : keyof IntelligenceCustomerFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.load();
  }

  toggleFilter ( field : keyof IntelligenceCustomerFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  topCustomersCsvRows () : Array<any> {
    return this.topCustomers.map(customer => ({
      customer           : customer.display_name,
      invoice_count      : customer.invoice_count,
      revenue_ex_tax     : customer.revenue_ex_tax,
      avg_per_invoice    : Math.round(this.averageInvoice(customer) * 100) / 100,
      gross_profit_ex_tax: customer.gross_profit_ex_tax
    }));
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topCustomers = this.intelligenceFormatService.sortRows(this.topCustomers, this.sort.field, this.sort.direction);
  }

  // only period filters can fall away; true means refetch the narrowed payload
  private dropFiltersMissingFromOptions () : boolean {
    const kept = {
      customer: this.intelligenceFormatService.keepIfOffered(this.filters.customer, this.filterOptions.customers),
      source  : this.intelligenceFormatService.keepIfOffered(this.filters.source, this.filterOptions.sources),
      suburb  : this.intelligenceFormatService.keepIfOffered(this.filters.suburb, this.filterOptions.suburbs)
    };
    const dropped = kept.customer !== this.filters.customer
      || kept.source !== this.filters.source
      || kept.suburb !== this.filters.suburb;

    this.filters = { ...this.filters, ...kept };

    return dropped;
  }

  private buildSourceSegments ( counts : { [ label : string ] : number }, isDrillable : boolean = false, options : Array<string> = null ) : Array<DonutSegmentModel> {
    const entries = Object.keys(counts)
      .map(label => ({ count: counts[ label ] || 0, label }))
      .sort(( first, second ) => second.count - first.count);

    const kept = entries.slice(0, DONUT_COLOR_COUNT);
    const remainder = entries.slice(DONUT_COLOR_COUNT).reduce(( sum, entry ) => sum + entry.count, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( entry, index ) => ({
      color: donutColor(entry.label, options, index),
      isDrillable,
      key  : entry.label,
      label: entry.label,
      value: entry.count
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : donutOtherColor(),
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: entries.slice(DONUT_COLOR_COUNT).map(( entry, index ) => ({
          color: donutColor(entry.label, options, DONUT_COLOR_COUNT + index),
          isDrillable,
          key  : entry.label,
          label: entry.label,
          value: entry.count
        })),
        value : remainder
      });
    }

    return segments;
  }
}
